import {inject, Injectable} from "@angular/core";
import {DataStorageService} from "src/app/infrastructure/data-storage.service";
import {Device, DeviceAssociation, SynchronizationStatus} from "src/app/infrastructure/moovement-api/models/asset";
import {DeviceAssociationStore} from "src/app/domain/devices/device-association.store";
import {DeviceApiClient} from "src/app/infrastructure/moovement-api/web-api/device.api-client";
import {DataType} from "src/app/domain/domain-models";
import {map, tap} from "rxjs";

@Injectable({
  providedIn: 'root'
})
export class DeviceAssociationService {

  private deviceAssociationStore = inject(DeviceAssociationStore);

  constructor(
    private dataStorageService: DataStorageService,
    private deviceApiClient: DeviceApiClient
  ) {}

  reset() {
    this.deviceAssociationStore.reset();
  }

  loadDeviceAssociations(farmId: string, devices: Device[]): void {
    const deviceIds = devices.map(device => device.id)
    this.deviceApiClient.getDeviceAssociations(deviceIds).subscribe({
      next: async deviceAssociations => {
        const activeDeviceAssociations = deviceAssociations.filter(deviceAssociation => deviceAssociation.endDateTime === undefined);
        for (const deviceAssociation of activeDeviceAssociations) {
          await this.persist(farmId, deviceAssociation);
        }
        this.deviceAssociationStore.setAllDeviceAssociations(activeDeviceAssociations);
      },
      error: async () => {
        const cachedDeviceAssociations = await this.dataStorageService.getAllActiveAssetsMatching<DeviceAssociation>(DataType.deviceAssociations, farmId);
        this.deviceAssociationStore.setAllDeviceAssociations(cachedDeviceAssociations);
      }
    });
  }

  private async persist(farmId: string, deviceAssociation: DeviceAssociation) {
    await this.dataStorageService.saveAsset(DataType.deviceAssociations, farmId, deviceAssociation, SynchronizationStatus.upToDate);
  }

  dissociateDevice(deviceId: string) {
    return this.deviceApiClient.dissociateDevice(deviceId).pipe(
      tap(das =>
        das.forEach(da => {
          this.deviceAssociationStore.removeDeviceAssociation(da.id);
          this.dataStorageService.deleteAsset(da.id);
        })
      )
    );
  }

  associateDevice(farmId: string, deviceId: string, assetId: string) {
    return this.deviceApiClient.associateDevice(deviceId, assetId).pipe(
      tap(das =>
        das.forEach(da => {
          if (da.endDateTime) {
            this.deviceAssociationStore.removeDeviceAssociation(da.id);
            this.dataStorageService.deleteAsset(da.id);
          } else {
            this.deviceAssociationStore.addDeviceAssociation(da);
            this.persist(farmId, da);
          }
        })
      ),
      map(das => undefined)
    );
  }

  mergeAssociation(farmId: string, deviceAssociation: DeviceAssociation) {
    return this.deviceApiClient.mergeAssociation(deviceAssociation).pipe(
      tap(da => {
        this.deviceAssociationStore.setDeviceAssociation(da);
        this.persist(farmId, da);
      }),
      map(das => undefined)
    );
  }
}
